import type {Ref} from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from '@chekinapp/ui';
import {RegisteringView} from 'components/RegistrationResult/RegisteringView';
import {PropertyLinkShell} from 'pages/SearchReservation/components/PropertyLinkShell';
import type {GuestErrors} from 'hooks/auth/useGuestCreation';

export type RegisteringPhase = 'sending' | 'confirmed' | 'error';

const VIEW_PHASE = {
  sending: 'sending',
  confirmed: 'succeeded',
  error: 'errored',
} as const;

type Props = {
  columnRef: Ref<HTMLDivElement>;
  phase: RegisteringPhase;
  who: string;
  registeredCount: number;
  guestErrors: GuestErrors;
  onRetry: () => void;
  onUseForm: () => void;
};

function RegisteringStep({
  columnRef,
  phase,
  who,
  registeredCount,
  guestErrors,
  onRetry,
  onUseForm,
}: Props) {
  const {t} = useTranslation();
  const isError = phase === 'error';
  const errorMessages = Object.entries(guestErrors).flatMap(([name, messages]) =>
    messages.map(message => `${name}: ${message}`),
  );

  return (
    <PropertyLinkShell
      columnRef={columnRef}
      dock={
        isError && (
          <>
            <Button className="w-full" onClick={onRetry}>
              {t('try_again')}
            </Button>
            <Button variant="ghost" className="w-full" onClick={onUseForm}>
              {t('instant_check_in.fill_form_instead')}
            </Button>
          </>
        )
      }
    >
      <RegisteringView
        phase={VIEW_PHASE[phase]}
        firstName={who}
        succeededTitle={t('instant_check_in.registered_title', {count: registeredCount})}
        footnote={t('instant_check_in.secure_note')}
      />

      {isError && errorMessages.length > 0 && (
        <ul className="mt-4 flex flex-col gap-1.5 rounded-xl bg-[var(--chekin-danger-surface)] p-3 text-[13px] text-[var(--chekin-color-brand-navy)]">
          {errorMessages.map(message => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </PropertyLinkShell>
  );
}

export {RegisteringStep};
